import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { APPOINTMENT_CANCEL_REASON, type AppointmentCancelReason } from '@his/shared';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const CANCEL_REASON_LABELS: Record<AppointmentCancelReason, string> = {
  patient_request: 'Patient asked to cancel',
  patient_unwell: 'Patient is unwell',
  doctor_unavailable: 'Doctor is not available',
  booked_in_error: 'Booked by mistake',
  clinic_closed: 'Clinic closed',
  rebooked: 'Moved to another appointment',
  other: 'Other reason',
};

export interface CancellableAppointment {
  id: string;
  slotAt: string;
  doctor: { name: string };
  patient: { fullName: string };
}

export function CancelAppointmentDialog({
  appointment,
  onClose,
  onDone,
}: {
  appointment: CancellableAppointment;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reasonCode, setReasonCode] = useState<AppointmentCancelReason | ''>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: () =>
      put<{ cancelledInvoices?: number; refundNeeded?: boolean }>(`/appointments/${appointment.id}/status`, {
        status: 'cancelled',
        reasonCode,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (result) => {
      if (result.refundNeeded) {
        toast.warning(tr('opd', 'appointment_cancelled_a_refund_is_due'), tr('opd', 'the_fee_was_already_paid_record_the_refund_then'));
      } else if (result.cancelledInvoices) {
        toast.success(tr('opd', 'appointment_cancelled'), tr('opd', 'x_unpaid_fee_invoice_s_were_cancelled_with_it', { cancelledInvoices: result.cancelledInvoices }));
      } else {
        toast.success(tr('opd', 'appointment_cancelled'));
      }
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const needsText = reasonCode === 'other' && reason.trim().length < 3;

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'cancel_the_appointment')}
      description={tr('opd', 'x_with_x_x', { fullName: appointment.patient.fullName, name: appointment.doctor.name, slotAt: dateTime(appointment.slotAt) })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'keep_it')}
          </Button>
          <Button variant="destructive" onClick={() => cancel.mutate()} loading={cancel.isPending} disabled={!reasonCode || needsText}>
            {tr('opd', 'cancel_appointment')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('opd', 'why_is_it_being_cancelled')} required>
          <Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value as AppointmentCancelReason | '')}>
            <option value="">{tr('opd', 'choose_a_reason')}</option>
            {APPOINTMENT_CANCEL_REASON.map((code) => (
              <option key={code} value={code}>
                {CANCEL_REASON_LABELS[code]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('opd', 'note')} required={reasonCode === 'other'}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
